import { useParams } from "react-router-dom";
import React, { Suspense } from "react";
import {
  SuspenseWithPerf,
  useAuth,
  useFirestore,
  useFirestoreCollectionData,
  useFirestoreDocData,
  useFirestoreDocDataOnce,
} from "reactfire";
import Helmet from "react-helmet";
import Skeleton from "@material-ui/lab/Skeleton";
import TicketReservationHeader from "../TicketReservation/TicketReservationHeader";
import Container from "@material-ui/core/Container";
import gameSelectors from "../../selectors/gameSelectors";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import GameAdminActions from "./GameAdminActions";
import GameAdminInfos from "./GameAdminInfos";
import Emoji from "a11y-react-emoji";
import Link from "@material-ui/core/Link";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import GameAdminEditModal from "./GameAdminEditModal";

function GameAdmin() {
  const auth = useAuth();
  const firestore = useFirestore();
  const { gameId } = useParams();
  const [editOpen, setEditOpen] = React.useState(false);

  const user = auth.currentUser;
  const usersRef = firestore.collection("users");
  const userDataRef = usersRef.doc(user.uid);
  const userData = useFirestoreDocDataOnce(userDataRef);

  const gameRef = firestore.collection("games").doc(gameId);
  const game = useFirestoreDocData(gameRef);

  const team = useFirestoreDocDataOnce(
    firestore.collection("teams").doc(game.teamId)
  );
  const opponent = useFirestoreDocDataOnce(
    firestore.collection("opponents").doc(game.opponentId)
  );
  const location = useFirestoreDocDataOnce(
    firestore.collection("locations").doc(game.locationId)
  );

  const ticketReservationsRef = gameRef.collection("ticketReservations");
  const ticketReservations = useFirestoreCollectionData(ticketReservationsRef);

  if (!userData.host && !userData.admin) {
    return <div>Kein Zugriff</div>;
  }

  if (gameId == null) {
    return <div>Ungültige Spielenummer</div>;
  }

  const { leftTeam, rightTeam } = gameSelectors.getLeftAndRightTeams(
    game,
    team,
    opponent
  );

  const { visitorCount = 0 } = game;
  const visitorSlots = game.visitorSlots || location.visitorSlots;

  return (
    <>
      <Helmet>
        <title>Spielverwaltung – SG Coesfeld 06</title>
      </Helmet>
      <SuspenseWithPerf
        traceId="TicketReservationHeader"
        fallback={<Skeleton width="100%" height={30} />}
      >
        <TicketReservationHeader
          game={game}
          leftTeam={leftTeam}
          rightTeam={rightTeam}
        />
      </SuspenseWithPerf>
      <Container maxWidth="sm">
        <GameAdminActions
          gameRef={gameRef}
          game={game}
          ticketReservations={ticketReservations}
        />
        <Divider />
        <Box mt={2} mb={2}>
          <Typography align="center" color="textSecondary" variant="subtitle2">
            Zuschauer in der Halle
          </Typography>
          <Box mt={0.5}>
            <Typography
              align="center"
              variant="h3"
              color={visitorCount >= visitorSlots ? "secondary" : "textPrimary"}
            >
              <b>
                {visitorCount} / {visitorSlots}
              </b>
              {userData.admin && (
                <>
                  {" "}
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="span"
                    onClick={() => setEditOpen(true)}
                  >
                    <EditIcon />
                  </IconButton>
                  <GameAdminEditModal
                    open={editOpen}
                    handleClose={() => setEditOpen(false)}
                    gameRef={gameRef}
                    game={game}
                    location={location}
                  />
                </>
              )}
            </Typography>
          </Box>
        </Box>
        <Suspense
          fallback={<Skeleton variant="rect" width="100%" height={100} />}
        >
          <GameAdminInfos
            game={game}
            location={location}
            ticketReservations={ticketReservations}
          />
        </Suspense>
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            Wenn ein Check-In falsch durchgeführt wurde, einfach erneut
            durchführen. Es wird automatisch korrigiert.
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            Die Tickets verfallen <b>nicht</b> automatisch 15 Minuten vor
            Spielbeginn im System, das muss man manuell umdenken.
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            Wenn du Schwierigkeiten hast, ruf einfach an <Emoji symbol="🙂" />:{" "}
            <Link onClick={() => window.open("tel:+491603267505", "_blank")}>
              +491603267505
            </Link>
          </Typography>
        </Box>
      </Container>
    </>
  );
}

export default GameAdmin;
